import {
  CartesianCoordinate,
  Coordinate,
  CylindricalCoordinate as CylindricalCoordinate,
} from "./Coordinate";
import { ECarrier } from "./ECarrier";

export enum EWaypointDirection {
  Forward = 0,
  Reverse = 1,
}

export type Vector = {
  axisType: EAxisType;
  coordinate: Coordinate;
  velocity: Coordinate;
  isAbsolute: boolean;
  name: string;
};

export interface CartesianVector {
  coordinate: CartesianCoordinate;
  velocity: CartesianCoordinate;
  isAbsolute: boolean;
  name: string;
}

export interface CylindricalVector {
  coordinate: CylindricalCoordinate;
  velocity: CylindricalCoordinate;
  isAbsolute: boolean;
  name: string;
}

export enum EAxisType {
  Cartesian,
  Cylindrical,
}

export interface Waypoint {
  axisType: EAxisType;
  predefined: boolean;
  supportAutoCalibration: boolean;
  isOverriden: boolean;
  carrier: ECarrier;
  vectors: Array<Vector>;
  forwardOffset: CartesianCoordinate | CylindricalCoordinate;
  reverseOffset: CartesianCoordinate | CylindricalCoordinate;
  name: string;
  armType: EArmType;
  defaultVelocity: CartesianCoordinate | CylindricalCoordinate;
  ensureXAxisIsSafe?: boolean;
}

export function createNewCylindricalWaypoint(
  waypointName: string,
  armType: EArmType,
  carrier: ECarrier,
  defaultVelocity: CylindricalCoordinate
) {
  const newWaypoint: CylindricalWaypoint = {
    name: waypointName,
    armType: armType,
    carrier: carrier,
    predefined: false,
    supportAutoCalibration: false,
    isOverriden: false,
    vectors: [
      {
        coordinate: {
          t: 0,
          e: 0,
          z: 0,
        },
        isAbsolute: false,
        velocity: defaultVelocity,
        name: "Position 1",
      },
    ],
    forwardOffset: {
      t: 0,
      e: 0,
      z: 0,
    },
    reverseOffset: {
      t: 0,
      e: 0,
      z: 0,
    },
    defaultVelocity: {
      t: 0,
      e: 0,
      z: 0,
    },
  };

  return newWaypoint;
}

export function createNewCartesianWaypoint(
  waypointName: string,
  armType: EArmType,
  carrier: ECarrier,
  defaultVelocity: CartesianCoordinate
) {
  const newWaypoint: CartesianWaypoint = {
    name: waypointName,
    armType: armType,
    carrier: carrier,
    predefined: false,
    supportAutoCalibration: false,
    isOverriden: false,
    vectors: [
      {
        coordinate: {
          x: 0,
          y: 0,
          z: 0,
        },
        isAbsolute: false,
        velocity: defaultVelocity,
        name: "Position 1",
      },
    ],
    forwardOffset: {
      x: 0,
      y: 0,
      z: 0,
    },
    reverseOffset: {
      x: 0,
      y: 0,
      z: 0,
    },
    defaultVelocity: {
      x: 0,
      y: 0,
      z: 0,
    },
    ensureXAxisIsSafe: false
  };

  return newWaypoint;
}

export function toCylindricalVector(vector: Vector) {
  const cartesian: CylindricalVector = {
    coordinate: toCylindricalCoordinate(vector.coordinate),
    velocity: toCylindricalCoordinate(vector.velocity),
    isAbsolute: vector.isAbsolute,
    name: vector.name,
  };
  return cartesian;
}

export function toCylindricalCoordinate(coordinate: Coordinate) {
  if (coordinate.t == null) {
    throw new Error("T is null");
  }
  if (coordinate.e == null) {
    throw new Error("E is null");
  }
  const cartesian: CylindricalCoordinate = {
    t: coordinate.t,
    e: coordinate.e,
    z: coordinate.z,
  };
  return cartesian;
}

export function toCylindricalWaypoint(waypoint: Waypoint) {
  const cartesian: CylindricalWaypoint = {
    predefined: waypoint.predefined,
    supportAutoCalibration: waypoint.supportAutoCalibration,
    isOverriden: waypoint.isOverriden,
    carrier: waypoint.carrier,
    vectors: waypoint.vectors.map((v) => toCylindricalVector(v)),
    forwardOffset: toCylindricalCoordinate(waypoint.forwardOffset),
    reverseOffset: toCylindricalCoordinate(waypoint.reverseOffset),
    name: waypoint.name,
    armType: waypoint.armType,
    defaultVelocity: toCylindricalCoordinate(waypoint.defaultVelocity),
  };

  return cartesian;
}

export function toVector(vector: CartesianVector | CylindricalVector) {
  const newVector: Vector = {
    axisType: IsCartesianVector(vector)
      ? EAxisType.Cartesian
      : EAxisType.Cylindrical,
    coordinate: vector.coordinate,
    velocity: vector.velocity,
    isAbsolute: vector.isAbsolute,
    name: vector.name,
  };
  return newVector;
}

export function toWaypoint(waypoint: CylindricalWaypoint | CartesianWaypoint) {
  let newCartesian: Waypoint = {
    axisType: IsCartesianWaypoint(waypoint)
      ? EAxisType.Cartesian
      : EAxisType.Cylindrical,
    predefined: waypoint.predefined,
    supportAutoCalibration: waypoint.supportAutoCalibration,
    isOverriden: waypoint.isOverriden,
    carrier: waypoint.carrier,
    vectors: [] as Vector[],
    forwardOffset: waypoint.forwardOffset,
    reverseOffset: waypoint.reverseOffset,
    name: waypoint.name,
    armType: waypoint.armType,
    defaultVelocity: waypoint.defaultVelocity,
    ensureXAxisIsSafe: waypoint.ensureXAxisIsSafe
  };

  for (let i = 0; i < waypoint.vectors.length; i++) {
    newCartesian.vectors.push(toVector(waypoint.vectors[i]));
  }

  return newCartesian;
}

export function toCartesianVector(vector: Vector) {
  const cartesian: CartesianVector = {
    coordinate: toCartesianCoordinate(vector.coordinate),
    velocity: toCartesianCoordinate(vector.velocity),
    isAbsolute: vector.isAbsolute,
    name: vector.name,
  };
  return cartesian;
}

export function toCartesianCoordinate(coordinate: Coordinate) {
  if (coordinate.x == null) {
    throw new Error("X is null");
  }
  if (coordinate.y == null) {
    throw new Error("Y is null");
  }
  const cartesian: CartesianCoordinate = {
    x: coordinate.x,
    y: coordinate.y,
    z: coordinate.z,
  };
  return cartesian;
}

export function toCartesianWaypoint(waypoint: Waypoint) {
  const cartesian: CartesianWaypoint = {
    predefined: waypoint.predefined,
    supportAutoCalibration: waypoint.supportAutoCalibration,
    isOverriden: waypoint.isOverriden,
    carrier: waypoint.carrier,
    vectors: waypoint.vectors.map((v) => toCartesianVector(v)),
    forwardOffset: toCartesianCoordinate(waypoint.forwardOffset),
    reverseOffset: toCartesianCoordinate(waypoint.reverseOffset),
    name: waypoint.name,
    armType: waypoint.armType,
    defaultVelocity: toCartesianCoordinate(waypoint.defaultVelocity),
    ensureXAxisIsSafe: waypoint.ensureXAxisIsSafe
  };

  return cartesian;
}

export function IsCartesianWaypoint(
  waypoint: CartesianWaypoint | CylindricalWaypoint
) {
  return (waypoint.forwardOffset as CartesianCoordinate).x !== undefined;
}

export function IsCartesianVector(vector: CartesianVector | CylindricalVector) {
  return (vector as CartesianVector).coordinate.x !== undefined;
}

export interface CartesianWaypoint {
  predefined: boolean;
  supportAutoCalibration: boolean;
  isOverriden: boolean;
  carrier: ECarrier;
  vectors: CartesianVector[];
  forwardOffset: CartesianCoordinate;
  reverseOffset: CartesianCoordinate;
  name: string;
  armType: EArmType;
  defaultVelocity: CartesianCoordinate;
  ensureXAxisIsSafe?: boolean;
}

export interface CylindricalWaypoint {
  predefined: boolean;
  supportAutoCalibration: boolean;
  isOverriden: boolean;
  carrier: ECarrier;
  vectors: CylindricalVector[];
  forwardOffset: CylindricalCoordinate;
  reverseOffset: CylindricalCoordinate;
  name: string;
  armType: EArmType;
  defaultVelocity: CylindricalCoordinate;
  ensureXAxisIsSafe?: boolean;
}

export enum EArmType {
  ImagingArm = 0,
  PsrArm = 1,
}

export interface ModuleWaypoint {
  moduleIdentifier: string;
  cartesianWaypoints?: CartesianWaypoint[];
  cylindricalWaypoints?: CylindricalWaypoint[];
}
